import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface PageTypeDefault {
  id?: string;
  page_type: string;
  title_pattern: string;
  description_pattern: string;
  og_image_strategy: string;
  index_default: boolean;
  follow_default: boolean;
}

const PAGE_TYPES = [
  { value: 'resume', label: 'Resume', icon: 'person', variables: '{{name}}, {{headline}}, {{summary}}, {{siteName}}' },
  { value: 'project', label: 'Project', icon: 'folder', variables: '{{projectName}}, {{description}}, {{siteName}}' },
  { value: 'case_study', label: 'Case Study', icon: 'description', variables: '{{title}}, {{description}}, {{siteName}}' },
  { value: 'writing', label: 'Writing / Blog', icon: 'article', variables: '{{title}}, {{excerpt}}, {{siteName}}' },
  { value: 'static', label: 'Static Page', icon: 'web', variables: '{{pageTitle}}, {{siteName}}' },
];

export const SeoPageTypeDefaultsTab = () => {
  const queryClient = useQueryClient();
  const [defaults, setDefaults] = useState<Record<string, PageTypeDefault>>({});

  const { data: pageTypeDefaults, isLoading } = useQuery({
    queryKey: ['seo-page-type-defaults'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seo_page_type_defaults')
        .select('*');
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (pageTypeDefaults) {
      const mapped: Record<string, PageTypeDefault> = {};
      pageTypeDefaults.forEach((d) => {
        mapped[d.page_type] = {
          id: d.id,
          page_type: d.page_type,
          title_pattern: d.title_pattern || '',
          description_pattern: d.description_pattern || '',
          og_image_strategy: d.og_image_strategy || 'use_global_default',
          index_default: d.index_default,
          follow_default: d.follow_default,
        };
      });
      
      // Initialize missing page types
      PAGE_TYPES.forEach(pt => {
        if (!mapped[pt.value]) {
          mapped[pt.value] = {
            page_type: pt.value,
            title_pattern: '',
            description_pattern: '',
            og_image_strategy: 'use_global_default',
            index_default: true,
            follow_default: true,
          };
        }
      });
      
      setDefaults(mapped);
    }
  }, [pageTypeDefaults]);

  const saveMutation = useMutation({
    mutationFn: async (pageType: PageTypeDefault) => {
      const payload = {
        page_type: pageType.page_type,
        title_pattern: pageType.title_pattern || null,
        description_pattern: pageType.description_pattern || null,
        og_image_strategy: pageType.og_image_strategy,
        index_default: pageType.index_default,
        follow_default: pageType.follow_default,
      };

      if (pageType.id) {
        const { error } = await supabase
          .from('seo_page_type_defaults')
          .update(payload)
          .eq('id', pageType.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('seo_page_type_defaults')
          .insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-page-type-defaults'] });
      toast.success('Page type defaults saved');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  const updateDefault = (pageType: string, field: keyof PageTypeDefault, value: string | boolean) => {
    setDefaults(prev => ({
      ...prev,
      [pageType]: {
        ...prev[pageType],
        [field]: value,
      },
    }));
  };

  const handleSave = (pageType: string) => {
    if (defaults[pageType]) {
      saveMutation.mutate(defaults[pageType]);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-4 p-4 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground">
          Configure default SEO patterns for each page type. These settings will be used when no per-item override is set.
        </p>
      </div>

      <Accordion type="single" collapsible className="space-y-4">
        {PAGE_TYPES.map((pt) => {
          const pageDefault = defaults[pt.value];
          if (!pageDefault) return null;

          return (
            <AccordionItem key={pt.value} value={pt.value} className="border rounded-lg bg-card">
              <AccordionTrigger className="px-4 hover:no-underline">
                <div className="flex items-center gap-3">
                  <Icon name={pt.icon} size={20} className="text-muted-foreground" />
                  <span className="font-medium">{pt.label}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label>Title Pattern</Label>
                    <Input
                      value={pageDefault.title_pattern}
                      onChange={(e) => updateDefault(pt.value, 'title_pattern', e.target.value)}
                      placeholder={`{{pageTitle}} | {{siteName}}`}
                    />
                    <p className="text-xs text-muted-foreground">
                      Available variables: {pt.variables}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Description Pattern</Label>
                    <Input
                      value={pageDefault.description_pattern}
                      onChange={(e) => updateDefault(pt.value, 'description_pattern', e.target.value)}
                      placeholder="{{description}}"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>OG Image Strategy</Label>
                    <Select
                      value={pageDefault.og_image_strategy}
                      onValueChange={(value) => updateDefault(pt.value, 'og_image_strategy', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="use_item_image">Use Item Image</SelectItem>
                        <SelectItem value="use_global_default">Use Global Default</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between gap-8">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`${pt.value}-index`}
                        checked={pageDefault.index_default}
                        onCheckedChange={(checked) => updateDefault(pt.value, 'index_default', checked)}
                      />
                      <Label htmlFor={`${pt.value}-index`}>Allow indexing by default</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`${pt.value}-follow`}
                        checked={pageDefault.follow_default}
                        onCheckedChange={(checked) => updateDefault(pt.value, 'follow_default', checked)}
                      />
                      <Label htmlFor={`${pt.value}-follow`}>Allow following links by default</Label>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleSave(pt.value)}
                    disabled={saveMutation.isPending}
                    size="sm"
                    className="mt-2"
                  >
                    <Icon name="save" size={14} className="mr-2" />
                    Save {pt.label} Defaults
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

export default SeoPageTypeDefaultsTab;
